import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { AxiosResponse } from 'axios';

import optionsInfoState from '@/states/optionsInfoState';
import voipOptionInfoState from '@/states/voipOptionInfoState';
import hikariTvOptionInfoState from '@/states/hikariTvOptionInfoState';
import { unextOptionInfoState } from '@/states/unextOptionInfoState';
import alertState from '@/states/alertState';

import { OptionInfoResponse } from '@/types/api/mypage/options';

import api from '@/utils/api';
import { getSigV4HeadersForGet } from '@/utils/cognito';
import { ApiUrl, Constants } from '@/utils/constants';

import { RvoipOptionInfoState } from '@/states/rvoipOptionInfoState';
import useSetLoading from '../useSetLoading';

/** オプション利用状況取得APIのURL */
const apiUrl = ApiUrl.Read.OPTIONS_INFO;

/**
 * オプション利用状況取得APIの呼び出しフック。
 * オプション一覧レスポンスを用途別の state に分解し、各画面が必要部分だけ参照できるようにする。
 *
 * @returns execute - API実行関数
 */
const useCallAPIGetOptionInfo = () => {
  const setOptionsInfo = useSetRecoilState(optionsInfoState);
  const setVoipOptionInfo = useSetRecoilState(voipOptionInfoState);
  const setRVoipOptionInfo = useSetRecoilState(RvoipOptionInfoState);
  const setHikariTv = useSetRecoilState(hikariTvOptionInfoState);
  const setUnext = useSetRecoilState(unextOptionInfoState);
  const setAlert = useSetRecoilState(alertState);

  const { startLoading, finishLoading } = useSetLoading();

  const execute = useCallback(async () => {
    try {
      startLoading(apiUrl);
      const headers = await getSigV4HeadersForGet(apiUrl);
      const response: AxiosResponse<OptionInfoResponse, any> = await api.get(apiUrl, headers);

      // 総覧用の一覧情報と、個別画面用の詳細情報を分けて保持する。
      setOptionsInfo({
        option_inquiry_number: response.data.option_inquiry_number,
        options: response.data.options,
      });
      setVoipOptionInfo(response.data.voip);
      setRVoipOptionInfo(response.data.r_voip);
      setHikariTv(response.data.hikaritv);
      setUnext(response.data.unext);
    } catch (error: any) {
      // 取得失敗は個別分岐せず共通アラートで通知する。
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(apiUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useCallAPIGetOptionInfo;
